import { useCallback, useMemo } from 'react';
import { useField } from 'formik';
import { Plus, Trash2 } from 'lucide-react';

import { CollaboratorDraft, emptyCollaboratorDraft } from './collaboratorTypes';

interface AdditionalCollaboratorsFieldProps {
  /** Nom du champ Formik (CollaboratorDraft[]). */
  name: string;
}

const GERANT_OPTIONS = [
  { value: 'membre', label: 'Collaborateur' },
  { value: 'gerant', label: 'Gérant' },
] as const;

/**
 * Liste dynamique de collaborateurs additionnels (comptes ABP créés après la
 * création de l'acteur, via les endpoints PersonneMoraleAccess). Le fondateur
 * reste saisi dans la section Utilisateur — cette section est optionnelle.
 */
export const AdditionalCollaboratorsField: React.FC<AdditionalCollaboratorsFieldProps> = ({ name }) => {
  const [field, meta, helpers] = useField<CollaboratorDraft[]>(name);
  const collaborators = useMemo(() => field.value ?? [], [field.value]);

  const updateRow = useCallback(
    (index: number, patch: Partial<CollaboratorDraft>) => {
      const next = collaborators.map((c, i) => (i === index ? { ...c, ...patch } : c));
      helpers.setValue(next);
      helpers.setTouched(true);
    },
    [collaborators, helpers],
  );

  const addRow = useCallback(() => {
    helpers.setValue([...collaborators, emptyCollaboratorDraft()]);
  }, [collaborators, helpers]);

  const removeRow = useCallback(
    (index: number) => {
      helpers.setValue(collaborators.filter((_, i) => i !== index));
      helpers.setTouched(true);
    },
    [collaborators, helpers],
  );

  const inputClass =
    'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40';

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-gray-500">
        Comptes créés en plus de l&apos;utilisateur principal. Un mot de passe
        temporaire est généré pour chacun et affiché à la fin — pensez à les transmettre.
      </p>

      {collaborators.map((collaborator, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={index} className="grid grid-cols-12 gap-2 items-start">
          <div className="col-span-3">
            <input
              type="text"
              value={collaborator.firstName}
              onChange={(e) => updateRow(index, { firstName: e.target.value })}
              placeholder="Prénom"
              className={inputClass}
            />
          </div>
          <div className="col-span-3">
            <input
              type="text"
              value={collaborator.lastName}
              onChange={(e) => updateRow(index, { lastName: e.target.value })}
              placeholder="Nom"
              className={inputClass}
            />
          </div>
          <div className="col-span-3">
            <input
              type="email"
              value={collaborator.email}
              onChange={(e) => updateRow(index, { email: e.target.value })}
              placeholder="email@exemple.fr"
              className={inputClass}
            />
          </div>
          <div className="col-span-2">
            <select
              value={collaborator.isOwner ? 'gerant' : 'membre'}
              onChange={(e) => updateRow(index, { isOwner: e.target.value === 'gerant' })}
              className={inputClass}
            >
              {GERANT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-1 flex justify-center pt-1.5">
            <button
              type="button"
              onClick={() => removeRow(index)}
              className="text-gray-400 hover:text-red-600"
              aria-label="Retirer ce collaborateur"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      ))}

      {meta.touched && typeof meta.error === 'string' && (
        <p className="text-xs text-red-600">{meta.error}</p>
      )}

      <button
        type="button"
        onClick={addRow}
        className="inline-flex items-center gap-1.5 self-start rounded-lg border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600 hover:border-primary hover:text-primary"
      >
        <Plus size={14} />
        Ajouter un collaborateur
      </button>
    </div>
  );
};
